import { Controller, useFormContext } from "react-hook-form";
import { MultiSelect, type MultiSelectProps } from "../core/MultiSelect/MultiSelect";
import type { ConnectedFieldProps } from "./useFieldConnection";

export interface MultiSelectFieldProps
  extends Omit<MultiSelectProps, "value" | "onChange">,
    ConnectedFieldProps {}

/**
 * El `MultiSelect` ya cableado al formulario que lo envuelve.
 *
 * ── Por qué `Controller` y no `useFieldConnection` ──────────────────
 * Los demás campos de esta carpeta usan `register`, que engancha un
 * `<input>` nativo por su `ref` y lee su `value` del DOM. Aquí no hay
 * `<input>`: el valor es un ARRAY de cadenas y el control es un
 * `listbox`, así que no hay nada que registrar ni de dónde leerlo.
 *
 * `Controller` es la respuesta de react-hook-form a exactamente eso: un
 * campo cuyo valor no vive en el DOM. Todo lo demás sigue igual —el
 * error del campo, el `disabled` mientras se envía— así que desde fuera
 * se usa como cualquier otro campo de esta carpeta.
 *
 * Cualquier prop explícito GANA sobre lo que viene del formulario.
 */
export function MultiSelectField({
  name,
  registerOptions,
  disabled,
  error,
  ...rest
}: MultiSelectFieldProps) {
  const { control, formState } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={registerOptions}
      render={({ field, fieldState }) => (
        <MultiSelect
          {...rest}
          value={Array.isArray(field.value) ? (field.value as string[]) : []}
          onChange={field.onChange}
          disabled={disabled ?? formState.isSubmitting}
          error={
            error ??
            // Un mensaje de zod es `string`, pero el tipo de react-hook-form
            // admite `ReactNode`. Se filtra en vez de mentirle al control.
            (typeof fieldState.error?.message === "string"
              ? fieldState.error.message
              : undefined)
          }
        />
      )}
    />
  );
}
